import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';

const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    { q: 'What is Petition.io?', a: 'Petition.io is a decentralized petition and crowdfunding platform built on Ethereum. It allows anyone in the world to create, sign, and financially support petitions in a transparent, censorship-resistant way. Unlike traditional platforms, Petition.io does not control funds, signatures, or outcomes—everything is verifiable on-chain.' },
    { q: 'Does it cost anything to sign a petition?', a: 'Yes. Signing a petition costs a small amount of Ethereum, usually just a few cents. This fee is a blockchain transaction cost (gas) that helps prevent spam, bots, and fake signatures while keeping the system fully decentralized.' },
    { q: 'Can I advertise my petition?', a: 'Yes. Petition.io allows ethical promotion tools so petitions can reach more supporters through featured placement, sharing tools, and community visibility. All promotion is designed to remain transparent and free from hidden algorithmic manipulation.' },
    { q: 'Why get an ENS domain?', a: 'Getting an ENS domain gives you stronger reputation inside the Petition.io ecosystem. It makes your identity easier to verify, builds trust with supporters, and makes it cheaper and easier to create petitions in the future. Instead of using long wallet addresses, your identity becomes simple, readable, and reusable across Web3.' },
    { q: 'Why should I trust Petition.io?', a: 'Petition.io is built on public blockchain infrastructure, which means signatures, actions, and fundraising activity are transparent and tamper-resistant. Funds move directly between wallets without being held by a centralized company. This removes the risk of hidden manipulation, donation freezes, or behind-the-scenes control.' },
    { q: 'Does it cost anything to create a petition?', a: 'Yes. The cost to create a petition depends entirely on the current Ethereum transaction (gas) fees at the time of creation. Petition.io does charge a platform fee and the standard blockchain network cost.' },
    { q: 'Where do donations go?', a: 'All donations go directly to the wallet address specified by the petition creator. Petition.io never takes custody of donor funds, meaning your money cannot be frozen, redirected, or seized by the platform.' },
    { q: 'Does Petition.io store my personal data?', a: 'Petition.io does not use centralized servers to store user data. All petition content, records, and supporting data are stored directly on Arweave, a decentralized permanent storage network. This makes the data censorship-resistant, tamper-proof, and not owned or controlled by any single company. Your activity is tied to your wallet and ENS identity—not to traditional usernames, emails, or centralized accounts.' },
    { q: 'Can organizations and nonprofits use Petition.io?', a: 'Yes. Individuals, nonprofits, DAOs, activist groups, and global organizations can all use Petition.io to launch transparent campaigns.' },
    { q: 'What makes Petition.io different from Change.org or GoFundMe?', a: 'Traditional platforms are centralized and can freeze funds, censor campaigns, or change outcomes. Petition.io is decentralized, transparent, censorship-resistant, and removes third-party control over petitions and donations.' }
  ];

  return (
    <>
      <Helmet>
        <title>FAQ - Petition.io | Frequently Asked Questions</title>
        <meta name="description" content="Find answers to common questions about Petition.io, blockchain petitions, shares, fees, and how the platform works." />
        <meta property="og:title" content="Petition.io FAQ - Your Questions Answered" />
        <link rel="canonical" href="https://petition.io/faq" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30 py-20 pt-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-neutral-dark mb-4">
              Frequently Asked <span className="text-gradient">Questions</span>
            </h1>
            <p className="text-xl text-gray-600">Everything you need to know about Petition.io</p>
          </motion.div>

          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.05 }}
                className="bg-white rounded-xl shadow-card overflow-hidden"
              >
                <button
                  onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-lg text-neutral-dark pr-4">{faq.q}</span>
                  <motion.div
                    animate={{ rotate: openIndex === idx ? 45 : 0 }}
                    transition={{ duration: 0.3 }}
                    className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-blue/10 flex items-center justify-center text-primary-blue font-bold text-xl"
                  >
                    +
                  </motion.div>
                </button>
                <AnimatePresence>
                  {openIndex === idx && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-5 text-gray-600 leading-relaxed">
                        {faq.a}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default FAQPage;
